import {AfterViewChecked, Component, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {CardSettingsModel, DialogEventArgs, DragEventArgs, KanbanComponent} from "@syncfusion/ej2-angular-kanban";
import {KanbanBoardService} from "./kanban-board.service";
import {ActivatedRoute} from "@angular/router";
import {KanbanBoardCardsModel} from "./kanban-board-cards.model";
import {QueryFiltersComponent} from "../query-filters/query-filters.component";
import {TextBoxComponent} from '@syncfusion/ej2-angular-inputs';
import {Subscription} from "rxjs";
import {InternationalizationService} from "../../services/internationalization/internationalization.service";
import {$translate, $translateKey} from "../../services/internationalization/localize.service";
import {LocationService} from "../../services/location/location.service";
import {TransitionResponse} from "./kanban-board-configuration-response.model";
import {KanbanBoardConfigurationModel} from "./kanban-board-configuration.model";
import {SavedFiltersComponent} from "../saved-filters/saved-filters.component";
import {QueryFiltersSavedQueryModel} from "../query-filters/query-filters-saved-query.model";
import {LegacyNavigateService} from "../../services/legacy-navigate/legacy-navigate.service";
import {KanbanBoardQueryModel} from "./kanban-board-query.model";
import {AlertService} from "../../services/alert/alert.service";
import {KanbanBoardCardUpdateRequestModel} from "./kanban-board-card-update-request.model";
import {TableOperationService} from "../../services/table-operation/table-operation.service";
import {ViewDefinition} from "../../models/view/view-definition";
import {TableDefinition} from "../../models/table/table-definition";
import {KanbanCardContentModel} from "../../models/content/kanban-card-content-model";
import {TableEventPayload} from "../../models/event/table-event-payload";
import {EventEnvelope} from "../../models/event/event-envelope";

@Component({
  selector: 'sb-kanban',
  templateUrl: './kanban-board.component.html',
  styleUrls: ['./kanban-board.component.scss']
})
export class KanbanBoardComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('kanbanObj') kanbanObj: KanbanComponent;

  @ViewChild("advancedQueryFilters") advancedQueryFiltersObj: QueryFiltersComponent
  @ViewChild("savedFilters") savedFiltersObj: SavedFiltersComponent

  @ViewChild('textSearch') textSearch: TextBoxComponent;

  @ViewChild("headerTemplate")
  headerTemplate: TemplateRef<{}>;

  // Event / Subscription
  MessageBus = (window as any).KO_MESSAGE_BUS_BRIDGE;
  private localeChanged$: Subscription;
  private tableUpdateEventSubscription: any;
  private tableCreateEventSubscription: any;
  private modalRequiredClosedSubscription: any;
  private failedOperationSubscription: any;
  private recordSyncEventSubscription: any;

  //Board Config
  public cardSettings: CardSettingsModel = {
    selectionType: 'Single',
    // Needed to map the card in the drag and drop event
    headerField: 'id'
  };
  dialogSettings: any;
  allowDragAndDrop: boolean = false;
  transitions: TransitionResponse[]
  private kanbanId: string;
  config: KanbanBoardConfigurationModel
  private viewDefinition: ViewDefinition
  private tableDefinition: TableDefinition

  //Search
  loadAdvancedQueryFilters: boolean = false;
  showAdvancedFilters: boolean = false;

  TEXT_SEARCH_QUERY_PARAM: string = "text-search";
  query: KanbanBoardQueryModel = {
    advancedFiltersQuery: null,
    membersFilter: [],
    textSearchValue: null
  }

  // Board Data
  public kanbanData: KanbanBoardCardsModel[]
  public isTruncated: boolean;

  // Localised labels
  textSearchPlaceHolder: string;
  advancedFilterToggleTitle: string;
  addDueDateLabel: string;
  membersMenuLabel: string;
  private assigmentErrorMessage: string;
  private setDueDateErrorMessage: string;
  private statusChangeErrorMessage: string;
  private addMemberErrorMessage: string;
  private deleteMemberErrorMessage: string;
  private newCardDialogHeaderTitle: string;
  private pageNotFound: string;
  dataTruncatedWarning: string;
  private reloadingCardInfoMessage: string;
  private cardCreatedNotVisibleInfoMessage: string;

  dueDateFieldLocalisedName: string;

  constructor(private kanbanService: KanbanBoardService,
              private activatedRoute: ActivatedRoute,
              private i18nService: InternationalizationService,
              private locationService: LocationService,
              private navigateService: LegacyNavigateService,
              private alertService: AlertService,
              private tableOperationService :TableOperationService) {
  }

  async ngOnInit() {

    // Wipe out the page context
    (window as any).KO_PAGE_CONTEXT.clear();

    // Wipe out the Knockout content.
    const pageContentMain = document.getElementById("page-content-main");
    if (pageContentMain) {
      pageContentMain.innerHTML = "";
    }
    const errorConsole = document.getElementById("error-console");
    if (errorConsole) {
      errorConsole.innerHTML = "";
    }

    this.activatedRoute.paramMap.subscribe( params => {
      this.kanbanId = params.get("id");
    });
    this.activatedRoute.queryParams.subscribe(queryParams => {
        this.query.textSearchValue = queryParams[this.TEXT_SEARCH_QUERY_PARAM] || null
    })

    this.localeChanged$ = this.i18nService.localeChanged$.subscribe(async (locale: string) => {
      this.initialiseTranslations();
      await this.loadConfig();
    });

    this.initialiseTranslations();
    await this.loadConfig();

    await this.loadData();
    this.configureEventSubscriptions();
  }

  private configureEventSubscriptions() {
    // Records
    this.tableUpdateEventSubscription = this.MessageBus.subscribe(
      MessageBus.CHANNELS.TABLE_EVENT,
      TableEventPayload.getTopic(this.config.datasourceTable, "update"),
      async (data: TableEventPayload, envelope: EventEnvelope<TableEventPayload>) => {
        data?.id && await this.refreshCard(data.id);
      }
    );

    this.tableCreateEventSubscription = this.MessageBus.subscribe(
      MessageBus.CHANNELS.TABLE_EVENT,
      TableEventPayload.getTopic(this.config.datasourceTable, "create"),
      async (data: TableEventPayload, envelope: EventEnvelope<TableEventPayload>) => {
       const isCardVisible = data?.id && await this.refreshCard(data.id);
       if (isCardVisible === false) {
         //TODO: could add link to new card like in Jira
         this.alertService.info(this.cardCreatedNotVisibleInfoMessage);
       }
      }
    )

    this.modalRequiredClosedSubscription = this.MessageBus.subscribe(
      MessageBus.CHANNELS.NAVIGATION_EVENT,
      MessageBus.NAVIGATION_EVENTS.MODAL_REQUIRED_CLOSED,
      async (data: any, envelope: EventEnvelope<any>) => {
        //Only refresh after a delay to catch up modal cancellation events, this event will be triggered both on cancellation and when
        // modal is closed upon clicking workflow transition button, as we can't distinguish both actions
        data?.id && setTimeout(() => this.refreshCard(data?.id, true), 3000);
      });

    this.failedOperationSubscription = this.MessageBus.subscribe(
      MessageBus.CHANNELS.OPERATION_EVENT,
      MessageBus.OPERATION_EVENTS.FAILED,
      async (data: any, envelope: EventEnvelope<any>) => {
        this.alertService.error(this.statusChangeErrorMessage);
        data?.id && await this.refreshCard(data.id).then(() => this.alertService.info(this.reloadingCardInfoMessage));
        console.error('Failed to execute operation', (data?.errors || []).concat(data?.validationErrors || []));
      }
    );

    this.recordSyncEventSubscription = this.MessageBus.subscribe(
      MessageBus.CHANNELS.TABLE_EVENT,
      TableEventPayload.getTopic(this.config.datasourceTable, "sync"),
      async (data: any, envelope: EventEnvelope<any>)=> {
        data?.id && await this.refreshCard(data.id);
      }
    );
  }

  /**
   * Fetch latest card data and update the board accordingly
   * If the card doesn't match the query filters, hide the card by removing it from the board
   *
   * @param cardId record id of the card to refresh
   * @param onlyIfDirty if the card exists, only refresh card if card is flagged as dirty
   * @return id card is visible on the board after refresh
   */
  private async refreshCard(cardId: string, onlyIfDirty: boolean = false) {
    //TODO: Check last updated date ? or version to limit unneeded refreshes? Sync + update might be triggered at the same time
    const card = this.kanbanData.find(c => c.id === cardId);
    let cardExists = card != undefined
    if (!cardExists || (card && (!onlyIfDirty || card.isDirty))) {
      try {
        const boardData = await this.kanbanService.getBoardData(this.config, {
          ...this.query,
          recordId: cardId
        });
        const updatedCard = (boardData.cards || [])[0];
        updatedCard && this.kanbanService.transformCard(this.config, updatedCard);
        // Card will not be returned if it doesn't match current query filters
        let showCard = updatedCard != undefined;
        if (showCard) {
          cardExists ? this.kanbanObj.updateCard(boardData.cards[0]) : this.kanbanObj.addCard(boardData.cards[0]);
        } else {
          cardExists && this.kanbanObj.deleteCard(cardId);
        }
        return showCard;
      } catch (e) {
        console.error(`Failed to refresh record ${cardId} after update`, e);
        return;
      }
    }
  }

  ngAfterViewChecked(): void {
    // Columns style
    this.config?.columns.forEach(c => {
      if (c.color) {
        const columnHeader = document.querySelector(`.kanban-overview.e-kanban .e-kanban-table.e-header-table .e-header-row:not(.e-swimlane-row) th[data-key="${c.keyField}"]`) as HTMLElement
        if (columnHeader) {
          columnHeader.style.backgroundColor = c.color;
        }

       const columnsCells = document.querySelectorAll(`.kanban-overview.e-kanban .e-kanban-table.e-content-table .e-content-row:not(.e-swimlane-row) td[data-key="${c.keyField}"]`)
        columnsCells.forEach(columnCell => (columnCell as HTMLElement).style.backgroundColor = c.color)
      }
    })

    // Empty cards Style
    // Would be better to add a css class instead but couldn't make it work
    document.querySelectorAll('.kanban-overview.e-kanban .e-kanban-content .e-content-row .e-content-cells .e-card-wrapper .e-empty-card, .kanban-overview.e-kanban .e-kanban-content .e-content-row .e-content-cells .e-card-container .e-empty-card')
      .forEach(e => {
        let elemStyle = (e as HTMLElement).style
        elemStyle.borderRadius = '8px';
        elemStyle.border = '1px dashed var(--gray-300)';
        elemStyle.background = 'white';
        elemStyle.color = 'var(--gray-500)';
        elemStyle.display = 'block';
        elemStyle.padding = '12px';
        elemStyle.marginBottom = '12px';
        elemStyle.fontSize = '12px';
        elemStyle.fontWeight = '500px';
      });
  }

  private async loadConfig() {
    let config = await this.kanbanService.getConfiguration(this.kanbanId);
    if (!config) {
      this.alertService.errorMessage(this.pageNotFound);
    }
    this.setConfig(config);

    if (config.datasourceTable != null) {
      this.loadAdvancedQueryFilters = true;
    }
    this.dueDateFieldLocalisedName = config.dueDateI18nKey ? $translateKey(config.dueDateI18nKey) : null;

    // Somehow this never resolves using TableManager ... (window as any).TableManager.getRawTableDefinitionAsync(this.config.datasourceTable)
    // fetching directly
    const tdPromise  = fetch(`/_application/table_definition/${this.config.datasourceTable}`)
      .then(res => res.json())
      .catch(e => {
        console.error('Failed to to fetch table definition', e);
        return null
      });

    const vdPromise = fetch(`/_application/view_definition/${this.config.defaultDetailsViewDefId}/detail`)
      .then(res => res.json())
      .catch(e => {
        console.error('Failed to to fetch view_definition', e);
        return {viewDefinition: null}
      });

    [this.tableDefinition, {viewDefinition: this.viewDefinition}] = await Promise.all([tdPromise, vdPromise]);

    this.allowDragAndDrop = config.allowDragAndDrop;
  }

  private async loadData() {
    let boardData = await this.kanbanService.getBoardData(this.config, this.query)
    this.kanbanData = boardData.cards;
    this.isTruncated = boardData.isTruncated;
  }

  private async reloadData() {
    await this.loadData();
    this.kanbanObj.refresh();
  }

  ngOnDestroy(): void {
    this.localeChanged$ && this.localeChanged$.unsubscribe();
    this.tableUpdateEventSubscription && this.MessageBus.unsubscribe(this.tableUpdateEventSubscription);
    this.tableCreateEventSubscription && this.MessageBus.unsubscribe(this.tableCreateEventSubscription);
    this.modalRequiredClosedSubscription && this.MessageBus.unsubscribe(this.modalRequiredClosedSubscription);
    this.failedOperationSubscription && this.MessageBus.unsubscribe(this.failedOperationSubscription);
    this.recordSyncEventSubscription && this.MessageBus.unsubscribe(this.recordSyncEventSubscription);
  }

  async OnDragStop(dragEventArgs: DragEventArgs) {
    const cardAfterDrag = dragEventArgs.data[0] as KanbanBoardCardsModel;
    const cardBeforeDrag = this.kanbanObj.getCardDetails(dragEventArgs.element as HTMLElement) as KanbanBoardCardsModel;
    const columnKeyBeforeDrag = `${cardBeforeDrag.columnKey}`

    // When dragged over a forbidden column, the cardAfterDrag has the same column key as before the drag
    if (columnKeyBeforeDrag === cardAfterDrag.columnKey) {
      return;
    }

    if (this.config.hasWorkflow) {
      const validTransitions = this.config.transitions
        .filter( t => t.fromStatusCode == columnKeyBeforeDrag && t.toStatusCode == cardAfterDrag.columnKey)

      if (validTransitions.length == 0) {
        // To be safe, this transition should have already been managed in the kanban component column config
        dragEventArgs.cancel = true;
        console.warn("No transition found, opening record preview");
        this.previewRecord(cardAfterDrag);
        return;
      }

      let contentModel = new KanbanCardContentModel(this.viewDefinition, this.tableDefinition, cardAfterDrag.record);
      const workflowTransitions = this.tableOperationService.workflowButtons(contentModel)
        .filter(b => validTransitions.find(vd => vd.id === b.options?.operationId) != undefined)

      if (workflowTransitions.length > 1) {
        dragEventArgs.cancel = true;
        console.warn("Multiple transitions not supported, opening record preview");
        this.previewRecord(cardAfterDrag);
        return;
      }

      const workflowTransition = workflowTransitions[0]
      if (!workflowTransition) {
        dragEventArgs.cancel = true;
        console.warn("No transition found, opening record preview");
        this.previewRecord(cardAfterDrag);
        return;
      }

      workflowTransition?.actionCallback(dragEventArgs.event)
      // Mark the card to be refreshed in the case we don't pickup events also we can't easily distinguish if required modal close events
      // are linked to cancellation or a workflow button being clicked
      cardAfterDrag.isDirty = true
      return;
    }

    try {
      await this.kanbanService.updateCard(this.kanbanId, {id: cardAfterDrag.id, columnKey: cardAfterDrag.columnKey});
    }
    catch (e) {
      console.error(e);
      this.alertService.error(this.statusChangeErrorMessage);
      await this.refreshCard(cardAfterDrag.id);
    }
  }

  dialogOpen(args: DialogEventArgs): void {
    args.cancel = true;
  }

  async onApplyAdvancedFilters(query: string) {
    this.query.advancedFiltersQuery = query;
    await this.loadData();
    this.kanbanObj.refresh();
  }

  async onTextSearch(e: KeyboardEvent) {
    let searchValue: string = (<HTMLInputElement>e.target).value;
    this.query.textSearchValue = searchValue.trim();
    await this.search();
  }

  private initialiseTranslations() {
    this.textSearchPlaceHolder = $translate("ng.component.kanban.text-search")`Search this Board`;
    this.advancedFilterToggleTitle = $translate("ng.component.kanban.advanced-query-filters.filter")`Advanced Filters`;
    this.addDueDateLabel = $translate("ng.component.kanban.add-deadline")`Add Due Date`;
    this.membersMenuLabel = $translate("ng.component.kanban.members.filter")`Members`;

    this.assigmentErrorMessage = $translate("ng.component.kanban.set-assignee-error")`Failed to assign card`;
    this.setDueDateErrorMessage = $translate("ng.component.kanban.set-due-date-error")`Failed to update due date`;
    this.statusChangeErrorMessage = $translate("ng.component.kanban.change-status-error")`Failed to change status`;
    this.addMemberErrorMessage = $translate("ng.component.kanban.add-member-error")`Failed to add member`;
    this.deleteMemberErrorMessage = $translate("ng.component.kanban.delete-member-error")`Failed to delete member`;
    this.newCardDialogHeaderTitle = $translate("core.relatedlist.createnew.relation")`New`;
    this.pageNotFound = $translate("page.not.found")`Page not found`
    this.dataTruncatedWarning = $translate("ng.component.kanban.data.truncated")`Only a limited subset of records is displayed on the board. Please update the query to restrict the data`
    this.reloadingCardInfoMessage = $translate("ng.component.kanban.card.reloaded")`Card reloaded`
    this.cardCreatedNotVisibleInfoMessage = $translate("ng.component.kanban.card.created.not-visible.message")
    `<div>Changes are saved, but issue isn't visible</div>
    <div>For the record to be visible, it must match your query filters, and have a status that’s assigned to the board.</div>
    `
  }

  async onTextSearchKeyDown(e: KeyboardEvent) {
    if (e.key == 'Escape') {
      this.query.textSearchValue = ''
      await this.search();
    }
  }

  private async search() {
    await this.loadData();

    if (this.query.textSearchValue !== '') {
      let queryParams = {[this.TEXT_SEARCH_QUERY_PARAM]: this.query.textSearchValue }
      this.locationService.mergeLocationQueryParams(queryParams);
    } else {
      this.locationService.deleteLocationQueryParams([this.TEXT_SEARCH_QUERY_PARAM]);
    }
  }

  async onSaveQuery(queryToSave: QueryFiltersSavedQueryModel) {
    await this.savedFiltersObj.saveItem(queryToSave.name, queryToSave.query);
  }

  async onSelectSavedFilter(selectedQuery: QueryFiltersSavedQueryModel) {
    if(this.advancedQueryFiltersObj && selectedQuery.query) {
      await this.advancedQueryFiltersObj.applyQueryString(selectedQuery.query);
    }
  }

  async onClearSavedFilter() {
    await this.advancedQueryFiltersObj.clearQuery();
  }

  onCardClick(data: KanbanBoardCardsModel) {
    this.navigateService.preview(this.config.datasourceTable, data.id, null);
  }

  private previewRecord(data: KanbanBoardCardsModel) {
    this.navigateService.preview(this.config.datasourceTable, data.id);
  }

  private async updateCard(card: KanbanBoardCardsModel, cardUpdate: KanbanBoardCardUpdateRequestModel, onError: (e) => string) {
    try {
      let updatedCard = await this.kanbanService.updateCard(this.kanbanId, cardUpdate);
      if (updatedCard) {
        this.kanbanService.transformCard(this.config, updatedCard);
        this.kanbanObj.updateCard(updatedCard);
      } else {
        this.kanbanObj.deleteCard(updatedCard);
      }
    } catch (e) {
      let errorMessage = onError(e);
      this.alertService.error(errorMessage);
      this.kanbanObj.updateCard(card);
    }
  }

  async onAssigneeSelected(memberId: string | null, card: KanbanBoardCardsModel) {
    if (card.assignee?.userId !== memberId) {
      const previousAssignee = card.assignee
      if (memberId) {
        card.assignee = this.config.members.find(m => m.userId === memberId);
      } else {
        card.assignee = null;
      }

      await this.updateCard(card, {
        id: card.id,
        assigneeId: card.assignee?.userId || null
      }, (e) => {
        // Revert assignee
        card.assignee = previousAssignee
        return this.assigmentErrorMessage;
      })
    }
  }

    async onDueDateChanged(dueDate: Date | null, card: KanbanBoardCardsModel) {
    let previousDate = card.dueDateAsDate
    card.dueDateAsDate = dueDate

      await this.updateCard(card, {
        id: card.id,
        dueDate: dueDate
      }, (e) => {
        card.dueDateAsDate = previousDate
        return this.setDueDateErrorMessage
      });
    }

  async onMembersFilterChange(membersId: Array<string | null>) {
    this.query.membersFilter = membersId;
    await this.search();
  }

  OnDrag($event: DragEventArgs) {
    // Keep column background color on drag for the source
    document.querySelectorAll('.kanban-overview.e-kanban .e-kanban-content .e-content-row .e-content-cells .e-card-wrapper .e-target-dragged-clone, .kanban-overview.e-kanban .e-kanban-content .e-content-row .e-content-cells .e-card-container .e-target-dragged-clone')
      .forEach(e => (e as HTMLElement).style.background = 'none');

    // Keep column background color of target column on drag
    document.querySelectorAll('.e-kanban .e-kanban-content .e-content-row .e-content-cells .e-card-wrapper .e-target-dropped-clone, .e-kanban .e-kanban-content .e-content-row .e-content-cells .e-card-container .e-target-dropped-clone')
      .forEach(e => (e as HTMLElement).style.background = 'none');
  }

  onCardFooterClick(e: MouseEvent) {
    // Prevent Card dialog update to display on footer as we have specific actions there
    e.stopImmediatePropagation();
  }

  async onNewMemberSelected(newMemberUserId: string) {
    try {
      const updatedConfig = await this.kanbanService.addMember(this.kanbanId, newMemberUserId);
      this.setConfig(updatedConfig);
    } catch (e) {
      this.alertService.error(this.addMemberErrorMessage)
    }
  }

  async onMembersToDelete(userMemberIdToDelete: string) {
    try {
      const updatedConfig = await this.kanbanService.deleteMember(this.kanbanId, userMemberIdToDelete);
      this.setConfig(updatedConfig);
    } catch (e) {
      this.alertService.error(this.deleteMemberErrorMessage)
    }
  }

  setConfig(config: KanbanBoardConfigurationModel) {
    config.columns.forEach(c => c.template = this.headerTemplate as unknown as string)
    this.config = config;
  }

  onAddCardClick(columnKey: string) {
    var tileOptions = {
      "tile": "DetailsAutoScaffold",
      "parameter-values":
        {
          table: this.config.datasourceTable,
          method: "metadata"
        },
      loadOptions: {
        cache: false,
        // Allows to track that the event originated from Kanban and that the default behaviour of the required field modal
        // to redirect to the record created page shouldn't apply here
        parentContext: "kanban",
      },
      title: this.newCardDialogHeaderTitle
    };

    this.navigateService.modalForm(tileOptions, (eventCallback) => {
      // content.css from mce style is leaching, when opening the modal, collapsing the borders of the kanban underlying tables
      // it also happens on the preview but somehow, on preview close mce style is not enforced anymore
      //TODO: should be a workaround until SER-4585 get fixed
      document.querySelectorAll('.e-kanban .e-kanban-table').forEach(e => (e as HTMLElement).style.borderCollapse = 'separate');
    });
  }

  displayAddButton(key: string) {
    return this.config?.columns?.find(c => c.keyField === key)?.displayAddButton
  }

  onQueryCellInfo($event: any) {
    // Prevent table border headers from being collapsed on re-render
    //TODO: should be a workaround until SER-4585 get fixed
    document.querySelectorAll('.e-kanban .e-kanban-table').forEach(e => (e as HTMLElement).style.borderCollapse = 'separate');
  }
}
